import type { AxiosPromise } from 'axios'

import type { Category } from '../api-types/CatalogCategory'
import type { SubCategory } from '../api-types/CatalogSubCategory'
import type { Product, Comment } from '../api-types/CatalogProduct'

import BaseApi from '../bridge-api/baseApi'
import type { CatalogAutoComplete } from '../api-types/CatalogAutoComplete'

/**
 * Recursively removes duplicate categories from the categories array.
 * @param categories The array of categories to clean.
 * @returns The cleaned array of categories.
 */
function getCleanedCategories(categories: SubCategory[]): SubCategory[] {
  // Use the `map()` method to iterate over the categories array and return a new array of cleaned categories.
  return categories.map(category => {
    // Recursively clean the subcategories of the current category.
    const cleanedSubcategories = getCleanedCategories(category.sub_categories)

    // Return a new category object with the cleaned subcategories.
    return {
      ...category,
      // Use the `filter()` method to remove any subcategory with the same ID as the current category.
      sub_categories: cleanedSubcategories.filter(
        subcategory => subcategory.id !== category.id
      ),
    }
  })
}

export class Catalog extends BaseApi {
  getBook(productId: number): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/library/v1/products/${productId}`,
      headers: {
        // FIXME: Back-end fails with an error 500 when we have an X-Login-Token header without X-Profile-Token, so we call it "anonymously" for now.
        'X-Login-Token': undefined,
      },
    })
  }

  getAuthor(id: number): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/author/${id}`,
    })
  }

  getNarrator(id: number): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/narrator/${id}`,
    })
  }

  getSeries(id: number): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/series/${id}`,
    })
  }

  getPopularSeries(
    format: 'ebook' | 'audiobook',
    page: number = 0,
    per: number = 12
  ): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/popular/series?format=${format}&page=${page}&per=${per}`,
    })
  }

  getPopularMagazines(page: number = 0, per: number = 12): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/popular/magazines?page=${page}&per=${per}`,
    })
  }

  getCategories(
    contentType: 'book' | 'magazine',
    format: 'ebook' | 'audiobook' | 'ebook,audiobook' | '' = ''
  ): AxiosPromise<Category[]> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/categories?content_type=${contentType}&format=${format}`,
    }).then(response => {
      response.data.categories = getCleanedCategories(response.data.categories)

      return response
    })
  }

  getProductsByAuthor(
    id: number,
    page: number = 0,
    per: number = 48
  ): AxiosPromise<Product[]> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/authors/${id}/products?page=${page}&per=${per}`,
    })
  }

  getProductsByNarrator(
    id: number,
    page: number = 0,
    per: number = 48
  ): AxiosPromise<Product[]> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/narrators/${id}/products?page=${page}&per=${per}`,
    })
  }

  getProductsBySeries(
    id: number,
    type: string = 'book',
    page: number = 0,
    per: number = 48,
    sort:
      | 'volume'
      | 'relevance'
      | 'average_rating'
      | 'published_date'
      | 'title'
      | 'authors' = 'volume'
  ): AxiosPromise<Product[]> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/series/${id}/products?page=${page}&per=${per}&content_type=${type}&sort=${sort}`,
    })
  }

  getProductsByCategory(
    id: number,
    page: number = 0,
    per: number = 48,
    format: string = 'ebook,audiobook', // 'ebook' or 'audiobook' or 'ebook,audiobook'
    language?: string
  ): AxiosPromise<Product[]> {
    const params = new URLSearchParams({
      page: page.toString(),
      per: per.toString(),
      format,
    })

    if (language) {
      params.append('language', encodeURIComponent(language))
    }

    const url = `/discovery/v1/categories/${id}/products?${params.toString()}`

    return this.http({
      method: 'get',
      url,
    })
  }

  getProductsByTopListCategory(
    categoryIds: number | number[],
    page: number = 0,
    per: number = 48
  ): AxiosPromise<Product[]> {
    const params = new URLSearchParams({
      page: page.toString(),
      per: per.toString(),
      categoryIds: Array.isArray(categoryIds)
        ? categoryIds.join(',')
        : categoryIds.toString(),
    })

    const url = `discovery/v1/top/category/list?${params.toString()}`

    return this.http({
      method: 'get',
      url,
    })
  }

  getProductsByRecommendations(
    id: number,
    page: number = 0,
    per: number = 12
  ): AxiosPromise<Product[]> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/recommendations?product_id=${id}&page=${page}&per=${per}`,
    })
  }

  getSeriesByCategory(
    id: string,
    page: number = 0,
    per: number = 48
  ): AxiosPromise<Category[]> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/categories/${id}/series?page=${page}&per=${per}`,
    })
  }

  getProductComments(
    productId: number,
    page: number = 0,
    per: number = 48
  ): AxiosPromise<Comment[]> {
    return this.http({
      method: 'get',
      url: `/library/v1/products/${productId}/comments?page=${page}&per=${per}`,
    })
  }

  getProductCategories(productId: number): AxiosPromise<Category[]> {
    return this.http({
      method: 'get',
      url: `/library/v1/products/${productId}/categories`,
    })
  }

  getCategoryInfo(categoryId: number): AxiosPromise<Category[]> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/categories/${categoryId}?content_type=book`,
    })
  }

  getProductCountries(productId: number): AxiosPromise<Category[]> {
    return this.http({
      method: 'get',
      url: `/library/v1/products/${productId}/countries`,
    })
  }

  searchBooks(
    searchPhrase: string,
    page: number = 0,
    per: number = 12,
    format: string = 'ebook,audiobook',
    sort: string = 'relevance'
  ): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/search/products/books?search_phrase=${encodeURIComponent(
        searchPhrase
      )}&page=${page}&per=${per}&format=${format}&sort=${sort}`,
    })
  }

  searchMagazines(
    searchPhrase: string,
    page: number = 0,
    per: number = 12,
    format: string = 'ebook,audiobook'
  ): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/search/products/magazines?search_phrase=${encodeURIComponent(
        searchPhrase
      )}&page=${page}&per=${per}&format=${format}`,
    })
  }

  /**
   * Autocomplete search phrase and return suggestions for books and magazines
   */
  autocomplete(search: string): AxiosPromise<CatalogAutoComplete> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/autocomplete?search_phrase=${encodeURIComponent(
        search
      )}`,
    })
  }

  /**
   * Used for the NX4 to NX5 migration.
   */
  getProductIdByBookId(bookId: number | string): AxiosPromise<{
    product_id: number
  }> {
    return this.http({
      method: 'get',
      url: `/library/v1/books/${bookId}/product`,
    })
  }

  /**
   * Used for the Youboox to NX5 migration.
   */
  getProductIdByYoubooxBookId(youbooxBookId: number | string): AxiosPromise<{
    product_id: number
  }> {
    return this.http({
      method: 'get',
      url: `/ybmigration/books/${youbooxBookId}/product`,
    })
  }

  getSearchEditorialEntries(page = 0, per = 12, language = 'en') {
    return this.http({
      method: 'get',
      url: `/discovery/v1/search/editorial_entries?page=${page}&per=${per}&language=${encodeURIComponent(language)}`,
    })
  }

  getEditorialEntries(page = 0, per = 12, language = 'en') {
    return this.http({
      method: 'get',
      url: `/discovery/v1/editorial_entries?page=${page}&per=${per}&language=${encodeURIComponent(language)}`,
    })
  }

  getSelectionProducts(
    id: number,
    page: number = 0,
    per: number = 48,
    format: string = 'ebook,audiobook',
    language: string = 'en',
    forKids: boolean = false
  ) {
    // Use the correct API version based on the `forKids` parameter.
    // FIXME: Maybe not mandatory to change the API version for kids.
    const profileType = forKids ? '&profile_type=KIDS' : ''
    const homeAge = forKids ? '&homeAge=5' : ''
    const url = `/discovery/v3/selections/${id}/products?page=${page}&per=${per}&format=${format}&language=${encodeURIComponent(language)}${profileType}${homeAge}`

    return this.http({
      method: 'get',
      url,
    })
  }

  getSelectionEntries(
    page: number = 0,
    per: number = 12,
    language: string = 'en'
  ): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v2/home_entries?page=${page}&per=${per}&language=${encodeURIComponent(language)}`,
    })
  }

  getTopLists(
    page: number = 0,
    per: number = 12,
    language: string = 'en'
  ): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `/discovery/v1/top_lists?page=${page}&per=${per}&language=${encodeURIComponent(language)}`,
    })
  }

  getTopListsForKids(
    page: number = 0,
    per: number = 12,
    age: number = 6,
    language: string = 'en'
  ): AxiosPromise<any> {
    return this.http({
      method: 'get',
      url: `discovery/v1/top_lists?page=${page}&per=${per}&language=${encodeURIComponent(language)}&profile_type=KIDS&age=${age}`,
    })
  }
}
